import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerLayout from '../components/CustomerLayout';
import { customerAPI } from '../lib/api';
import '../App.css';

export default function Tickets() {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    
    // Create ticket form
    const [newTicket, setNewTicket] = useState({
        subject: '',
        description: '',
        priority: 'Normal'
    });
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        fetchTickets();
    }, [filter]);

    const fetchTickets = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = filter !== 'all' ? { status: filter } : {};
            const response = await customerAPI.getTickets(params);
            setTickets(response.data.tickets || []);
        } catch (err) {
            console.error('Error fetching tickets:', err);
            setError(err.response?.data?.error || 'Failed to load tickets');
        }finally {
            setLoading(false);
        }
    };

    const viewTicket = (ticketId) => {
        navigate(`/tickets/${ticketId}`);
    };

    const createTicket = async (e) => {
        e.preventDefault();
        
        if (!newTicket.subject || !newTicket.description) {
            alert('Please fill in all required fields');
            return;
        }

        setCreating(true);
        try {
            await customerAPI.createTicket(newTicket);
            setShowCreateModal(false);
            setNewTicket({ subject: '', description: '', priority: 'Normal' });
            fetchTickets();
        } catch (err) {
            console.error('Error creating ticket:', err);
            alert(err.response?.data?.error || 'Failed to create ticket');
        } finally {
            setCreating(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'open': return '#007bff';
            case 'in progress': return '#ffc107';
            case 'resolved': return '#28a745';
            case 'closed': return '#6c757d';
            default: return '#6c757d';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'low': return '#28a745';
            case 'normal': return '#007bff';
            case 'high': return '#ffc107';
            case 'urgent': return '#dc3545';
            default: return '#007bff';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };
    return (
        <CustomerLayout>
            <div style={{ maxWidth: '1200px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <div>
                        <h1>Support Tickets</h1>
                        <p style={{ color: '#666', marginTop: '8px' }}>Create and manage your support tickets</p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <select
                            className="form-input"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            style={{ width: 'auto' }}
                        >
                            <option value="all">All Tickets</option>
                            <option value="Open">Open</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Resolved">Resolved</option>
                            <option value="Closed">Closed</option>
                        </select>
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowCreateModal(true)}
                        >
                            Create Ticket
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-error">{error}</div>
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                        <div className="spinner"></div>
                        <p style={{ marginTop: '20px', color: '#666' }}>Loading tickets...</p>
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
                        <p style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>No tickets found</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowCreateModal(true)}
                        >
                            Create Your First Ticket
                        </button>
                    </div>
                ) : (
                    <div className="card">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Ticket #</th>
                                    <th>Subject</th>
                                    <th>Priority</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Last Updated</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tickets.map((ticket) => (
                                    <tr key={ticket.ticket_id}>
                                        <td><strong>{ticket.ticket_number}</strong></td>
                                        <td>{ticket.subject}</td>
                                        <td>
                                            <span
                                                className="status-badge"
                                                style={{ backgroundColor: getPriorityColor(ticket.priority), color: 'white' }}
                                            >
                                                {ticket.priority}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                className="status-badge"
                                                style={{ backgroundColor: getStatusColor(ticket.status), color: 'white' }}
                                            >
                                                {ticket.status}
                                            </span>
                                        </td>
                                        <td>{formatDate(ticket.created_at)}</td>
                                        <td>{formatDate(ticket.updated_at)}</td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-primary"
                                                onClick={() => viewTicket(ticket.ticket_id)}
                                                style={{ fontSize: '12px', padding: '6px 12px' }}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Create Ticket Modal */}
                {showCreateModal && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000
                        }}
                        onClick={() => setShowCreateModal(false)}
                    >
                        <div
                            style={{
                                background: 'white',
                                borderRadius: '8px',
                                maxWidth: '600px',
                                width: '90%',
                                padding: '30px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h2 style={{ marginBottom: '20px' }}>Create Support Ticket</h2>
                            <form onSubmit={createTicket}>
                                <div className="form-group">
                                    <label className="form-label">Subject *</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={newTicket.subject}
                                        onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                                        required
                                        placeholder="Brief description of your issue"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Priority</label>
                                    <select
                                        className="form-input"
                                        value={newTicket.priority}
                                        onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                                    >
                                        <option value="Low">Low</option>
                                        <option value="Normal">Normal</option>
                                        <option value="High">High</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Description *</label>
                                    <textarea
                                        className="form-input"
                                        value={newTicket.description}
                                        onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                                        required
                                        rows="6"
                                        placeholder="Please provide detailed information about your issue..."
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowCreateModal(false)}
                                        disabled={creating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={creating}
                                    >
                                        {creating ? 'Creating...' : 'Create Ticket'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </CustomerLayout>
    );
}
